// @flow

import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import classNames from "classnames";
import {
  append,
  contains,
  equals,
  findIndex,
  identity,
  indexBy,
  pipe,
  pick,
  prop,
  subtract,
  uniq,
  union,
  without,
} from "ramda";
import Downshift from "downshift";
import { useDebounce, useMount } from "react-use";

import { Translate, useI18n } from "@quantum/i18n";
import { SearchIcon, Spinner } from "@quantum/ui";

import { useScrollArea } from "../../../comum/Scroll";

import { useBuscaAtivos } from "../../../remote";

import {
  getAtivosCarteiraSelecionados,
  selecionaAtivosCarteira,
} from "../../../../state";

import "./Ativo.css";

const useDebouncedValue = (value, time = 2000) => {
  const [debouncedValue, setDebouncedValue] = React.useState(value);
  const [, cancel] = useDebounce(() => setDebouncedValue(value), time, [value]);
  return [debouncedValue, cancel, setDebouncedValue];
};

const stateReducer = (state: any, changes: any): any => {
  switch (changes.type) {
    case Downshift.stateChangeTypes.mouseUp:
      return {
        ...changes,
        isOpen: state.isOpen,
        inputValue: state.inputValue,
      };

    case Downshift.stateChangeTypes.keyDownEnter:
      return {
        ...changes,
        inputValue: state.inputValue,
        isOpen: state.isOpen,
        highlightedIndex: state.highlightedIndex,
      };

    case Downshift.stateChangeTypes.clickItem: {
      return {
        ...changes,
        inputValue: state.inputValue,
        isOpen: state.isOpen,
        highlightedIndex: state.highlightedIndex,
      };
    }

    case Downshift.stateChangeTypes.blurInput:
    case Downshift.stateChangeTypes.touchEnd:
    case Downshift.stateChangeTypes.itemMouseEnter:
      return {
        ...changes,
        inputValue: state.inputValue,
      };

    default:
      return changes;
  }
};

const takeOptionsBetween = (start, end, options) => {
  const startIndex = findIndex(equals(start), options);
  const endIndex = findIndex(equals(end), options);
  const [finalStart, finalEnd] = [startIndex, endIndex].sort(subtract);
  return options.slice(finalStart, finalEnd + 1);
};

const Ativo = () => {
  const { translate } = useI18n();

  const inputRef = React.useRef();
  const holdingShift = React.useRef(false);
  const lastSelection = React.useRef(null);
  const pivotSelecion = React.useRef(null);
  const lastPivotChange = React.useRef([]);

  const ativosSelecionados = useSelector(getAtivosCarteiraSelecionados);
  const value = ativosSelecionados;
  const dispatch = useDispatch();

  const [query, setQuery] = React.useState("");

  const focusInput = React.useCallback(() => {
    if (inputRef.current) inputRef.current.focus();
  }, [inputRef]);

  const [debouncedQuery, cancel, setDebouncedQuery] = useDebouncedValue(query);

  const {
    data: options = ativosSelecionados,
    loading,
    loaded = ativosSelecionados.length > 0,
    error,
  } = useBuscaAtivos({
    properties: { query: debouncedQuery },
    fetch: Boolean(debouncedQuery),
  });

  const opcoesIndexadas = indexBy(prop("id"), options);

  const onChange = React.useCallback(
    selecao => {
      const ativos = selecao
        .filter(({ id }) => opcoesIndexadas[id])
        .filter(Boolean);
      dispatch(selecionaAtivosCarteira(ativos));
    },
    [dispatch, opcoesIndexadas]
  );

  const onChangeInput = React.useCallback(e => {
    setQuery(e.target.value);
  }, []);

  const onKeyDown = React.useCallback(
    e => {
      if (e.keyCode === 13 || e.which === 13) {
        cancel();
        setDebouncedQuery(e.currentTarget.value);
      }
    },
    [setDebouncedQuery, cancel]
  );

  const onChangeHandler = React.useCallback(
    (selection, { inputValue }) => {
      if (!selection) return;

      if (!holdingShift.current) pivotSelecion.current = null;

      if (pivotSelecion.current) {
        const below = takeOptionsBetween(
          pivotSelecion.current,
          selection,
          options
        );
        const above = takeOptionsBetween(selection, pivotSelecion, options);
        const change = [...below, ...above];

        const withoutPrevious = without(lastPivotChange.current);
        const withPivot = append(pivotSelecion.current);

        onChange(pipe(withoutPrevious, union(change), withPivot, uniq)(value));
        lastPivotChange.current = change;
      } else if (contains(selection, value)) {
        onChange(without([selection], value));
        pivotSelecion.current = null;
        lastSelection.current = null;
        lastPivotChange.current = [];
      } else {
        onChange(append(selection, value));
        lastSelection.current = selection;
        lastPivotChange.current = [];
        if (pivotSelecion.current == null && holdingShift.current) {
          pivotSelecion.current = lastSelection.current;
        }
      }
      focusInput();
    },
    [
      value,
      onChange,
      focusInput,
      holdingShift,
      lastSelection,
      lastPivotChange,
      options,
    ]
  );

  React.useEffect(() => {
    pivotSelecion.current = null;
    lastSelection.current = null;
    lastPivotChange.current = [];
  }, [debouncedQuery, holdingShift, pivotSelecion, lastSelection]);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.shiftKey) {
        holdingShift.current = true;
        pivotSelecion.current = lastSelection.current;
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (!event.shiftKey) {
        holdingShift.current = false;
      }
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("keyup", onKeyUp);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("keyup", onKeyUp);
    };
  }, [holdingShift, pivotSelecion, lastSelection]);

  const {
    registerScrollArea,
    registerScrollTarget,
    scrollTo,
  } = useScrollArea();

  useMount(() => {
    if (value.length > 0) {
      const target = options.find(option =>
        value.map(prop("id")).includes(option.id)
      );
      if (target) scrollTo(target.id, { offsetTop: -30 });
    }
  });

  const mensagem = React.useMemo(() => {
    if ((debouncedQuery === "" || query === "") && options.length === 0) {
      return translate("configuracaoRelatorio.buscaAtivos.instrucao");
    } else if ((loaded && options.length === 0) || error) {
      return translate("configuracaoRelatorio.buscaAtivos.nenhumEncontrado");
    }
  }, [translate, query, debouncedQuery, options, loaded, error]);

  return (
    <div className="ConfiguracaoMedida">
      <label className="ConfiguracaoMedida__label">
        <Translate>Comum.Ativos</Translate>
      </label>

      <Downshift
        stateReducer={stateReducer}
        onSelect={onChangeHandler}
        itemToString={item => (item ? String(item.id) : "")}
      >
        {({
          getInputProps,
          getItemProps,
          getMenuProps,
          isOpen,
          inputValue,
          highlightedIndex,
          selectedItem,
        }) => (
          <div
            className={classNames(
              "q-Select",
              "q-Select--multiple",
              "ConfiguracaoAtivosCarteira"
            )}
          >
            <div
              className={classNames("q-SelectBody", "q-SelectBody--multiple")}
            >
              <div className="q-SelectBody__search">
                <SearchIcon />
                <input
                  type="text"
                  autoFocus
                  ref={inputRef}
                  {...getInputProps({
                    onChange: onChangeInput,
                    onKeyDown,
                  })}
                />
              </div>

              {loading && <Spinner />}

              {mensagem && !loading && (
                <div className="ConfiguracaoAtivosCarteira__mensagem">
                  {mensagem}
                </div>
              )}

              {!loading && (
                <ol
                  className="q-SelectBody__items"
                  {...getMenuProps({ ref: registerScrollArea })}
                  onScroll={focusInput}
                >
                  {options.map((item, index) => (
                    <li
                      ref={registerScrollTarget(item.id)}
                      key={item.id}
                      className={classNames(
                        "q-SelectItem",
                        "q-SelectItem--multiple",
                        {
                          "q-SelectItem--selected": contains(item, value),
                        }
                      )}
                      style={{
                        backgroundColor:
                          highlightedIndex === index
                            ? "#f3f9ff"
                            : "transparent",
                      }}
                    >
                      <label
                        {...pick(
                          ["onMouseMove"],
                          getItemProps({
                            index,
                            item,
                          })
                        )}
                      >
                        <input
                          type="checkbox"
                          {...getItemProps({
                            index,
                            item,
                            checked: contains(item, value),
                          })}
                          value={item.id}
                          onChange={identity}
                        />
                        {item.nome}
                      </label>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        )}
      </Downshift>
    </div>
  );
};

export default Ativo;
